import type { LucideIcon } from 'lucide-react';
import {
  EyeOff,
  FileText,
  Gauge,
  IdCard,
  Image,
  MonitorSmartphone,
  Server,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CLEAN_TEXT_AVAILABLE } from './clean-text/availability';

type Processing = 'device' | 'gemini' | 'server';

interface ToolCard {
  readonly id: string;
  readonly path: string;
  readonly icon: LucideIcon;
  readonly titleKey:
    | 'cards.identityDocuments.title'
    | 'cards.images.title'
    | 'cards.pdf.title'
    | 'cards.cleanText.title'
    | 'cards.deviceTest.title';
  readonly textKey:
    | 'cards.identityDocuments.text'
    | 'cards.images.text'
    | 'cards.pdf.text'
    | 'cards.cleanText.text'
    | 'cards.deviceTest.text';
  /** Where the file is processed: every card says it (#387, approved boards). */
  readonly processing: Processing;
}

/**
 * The tools that exist. A card is added with its tool's screen (#387: Immagini, PDF and the device
 * test follow), so the page never offers a tool that cannot open. Documenti d'identità keeps its
 * current address until it moves under /tools.
 */
const CLEAN_TEXT_CARD: ToolCard = {
  id: 'clean-text',
  path: '/tools/clean-text',
  icon: EyeOff,
  titleKey: 'cards.cleanText.title',
  textKey: 'cards.cleanText.text',
  processing: 'server',
};

const TOOL_CARDS: readonly ToolCard[] = [
  {
    id: 'images',
    path: '/tools/images',
    icon: Image,
    titleKey: 'cards.images.title',
    textKey: 'cards.images.text',
    processing: 'device',
  },
  {
    id: 'pdf',
    path: '/tools/pdf',
    icon: FileText,
    titleKey: 'cards.pdf.title',
    textKey: 'cards.pdf.text',
    processing: 'device',
  },
  ...(CLEAN_TEXT_AVAILABLE ? [CLEAN_TEXT_CARD] : []),
  {
    id: 'identity-documents',
    path: '/tools/identity-documents',
    icon: IdCard,
    titleKey: 'cards.identityDocuments.title',
    textKey: 'cards.identityDocuments.text',
    processing: 'gemini',
  },
  {
    id: 'device-test',
    path: '/tools/device-test',
    icon: Gauge,
    titleKey: 'cards.deviceTest.title',
    textKey: 'cards.deviceTest.text',
    processing: 'device',
  },
];

const PROCESSING_ICON: Record<Processing, LucideIcon> = {
  device: MonitorSmartphone,
  gemini: Sparkles,
  server: Server,
};

/** «Strumenti»: the module's landing page, one card per tool (#387, decision 98). */
export function ToolsPage() {
  const { t } = useTranslation('tools');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="screen-title">{t('landing.title')}</h2>
        <p className="screen-summary">{t('landing.summary')}</p>
      </div>

      <ul
        aria-label={t('landing.cardsLabel')}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        {TOOL_CARDS.map((card) => {
          const Icon = card.icon;
          const WhereIcon = PROCESSING_ICON[card.processing];
          return (
            <li key={card.id} className="min-w-0">
              <Link
                to={card.path}
                className="flex h-full flex-col gap-2.5 rounded-xl border border-border bg-card p-5
                  text-foreground no-underline transition-colors hover:bg-accent
                  focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                <span
                  aria-hidden="true"
                  className="flex size-11 items-center justify-center rounded-xl bg-selected text-selected-foreground"
                >
                  <Icon className="size-5" />
                </span>
                <span className="text-base font-semibold">{t(card.titleKey)}</span>
                <span className="text-sm text-muted-foreground">{t(card.textKey)}</span>
                <span className="mt-auto flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
                  <WhereIcon aria-hidden="true" className="size-3.5" />
                  {t(`processing.${card.processing}`)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>

      <p className="text-sm text-muted-foreground">{t('landing.searchHint')}</p>
    </div>
  );
}
